import { useEffect, useState, useMemo, useRef } from "react";
import { Link } from "react-router";

interface Option {
  label: string | React.ReactNode;
  value: any;
}
interface Props {
  options: Option[];
  defaultValue: any;
  value?: any;
  onChange: (value: any) => void;
  width?: string;
  placeholder?: string;
} 

const DropDown = ({ options, defaultValue, onChange, width, value, placeholder }: Props) => {
  const [selectedOption, setSelectedOption] = useState(defaultValue);
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const calculatedWidth = useMemo(() => {
    if (width) return width;
    // Limit the width calculation to prevent very wide dropdowns
    const maxWidth = 200; // Maximum width in pixels
    const minWidth = 120; // Minimum width in pixels
    
    const longestLabel = options.reduce(
      (max, option) => {
        const labelLength = typeof option.label === 'string' 
          ? option.label.length 
          : String(option.label).length;
        return labelLength > max ? labelLength : max;
      },
      0
    );
    
    const calculatedWidth = Math.min(longestLabel * 10, maxWidth);
    return `${Math.max(calculatedWidth, minWidth)}px`;
  }, [options, width]);

  useEffect(() => {
    setSelectedOption(value || defaultValue);
  }, [defaultValue, value]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const toggleDropdown = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsOpen(!isOpen);
  };

  // Function to truncate text with ellipsis
  const truncateText = (text: string | React.ReactNode, maxLength: number = 20) => {
    if (typeof text === 'string') {
      return text.length > maxLength ? `${text.substring(0, maxLength)}...` : text;
    }
    return text;
  };

  // Get the display text for the selected option
  const getDisplayText = () => {
    const selectedOptionData = options.find((opt) => opt.value === selectedOption);
    if (selectedOptionData) {
      return truncateText(selectedOptionData.label);
    }
    return placeholder || 'Select an option';
  };

  return (
    <div className="dropdown me-3 position-relative" ref={dropdownRef}>
      <Link
        to="#"
        className="dropdown-toggle btn btn-white d-inline-flex align-items-center"
        onClick={toggleDropdown}
        style={{ 
          width: calculatedWidth, 
          justifyContent: "space-between",
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap"
        }}
        title={typeof options.find((opt) => opt.value === selectedOption)?.label === 'string' 
          ? options.find((opt) => opt.value === selectedOption)?.label as string 
          : placeholder || 'Select an option'}
      >
        <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {getDisplayText()}
        </span>
      </Link>
      <ul className={`dropdown-menu dropdown-menu-end p-3 ${isOpen ? 'show' : ''}`}>
        {options.map((option, index) => (
          <li key={index}>
            <Link
              to="#"
              className="dropdown-item rounded-1"
              onClick={(e) => {
                e.preventDefault();
                setSelectedOption(option?.value);
                onChange(option?.value);
                setIsOpen(false);
              }}

            >
              {option?.label}
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
};

export default DropDown;